/* CUSTOM-KIRN: Current Drops — scroll-pinned product showcase.
 *
 * The carousel section is `position: sticky; top: 0; height: 100vh`
 * inside a tall `.kirn-drops-pin` wrapper. JS sets the wrapper height to
 * `(maxIndex + 1) * 100vh` so the page has enough native scroll
 * distance to advance through every card. As the user scrolls (wheel,
 * trackpad, or touch), the wrapper's bounding rect tells us the
 * progress; we translate the track proportionally. Once the user
 * scrolls past the pin, the next section flows in naturally; scrolling
 * back up rewinds the carousel first, then releases the page.
 *
 * This means every input — desktop wheel, trackpad swipe, mobile touch
 * — works without any event interception. The browser handles scroll
 * inertia; we just read scrollY and paint.
 */
(() => {
  const init = () => {
    document.querySelectorAll('[data-kirn-drops-pin]').forEach(setup);
  };

  const setup = (pin) => {
    const section = pin.querySelector('.kirn-drops');
    const viewport = pin.querySelector('[data-kirn-drops-viewport]');
    const track = pin.querySelector('[data-kirn-drops-track]');
    const bar = pin.querySelector('[data-kirn-drops-bar]');
    const prevBtn = pin.querySelector('[data-kirn-drops-prev]');
    const nextBtn = pin.querySelector('[data-kirn-drops-next]');
    if (!section || !viewport || !track) return;

    const cards = () => Array.from(track.children);

    let currentIndex = 0;
    let currentProgress = 0;

    /* --- Geometry --- */
    const maxOffset = () =>
      Math.max(0, track.scrollWidth - viewport.clientWidth);

    const maxIndex = () => {
      const list = cards();
      if (!list.length) return 0;
      const limit = maxOffset();
      if (limit === 0) return 0;
      for (let i = list.length - 1; i >= 0; i--) {
        if (list[i].offsetLeft <= limit + 1) return i;
      }
      return 0;
    };

    /* --- Pin height ---
     * Total scroll distance the user must travel through the pin to
     * advance from index 0 to maxIndex. We allocate 1 viewport height
     * per advance step, plus 1 viewport for the "rest" state at the
     * top. So pin.height = (maxIndex + 1) * 100vh. */
    const setPinHeight = () => {
      const max = maxIndex();
      const vh = window.innerHeight;
      pin.style.height = `${(max + 1) * vh}px`;
    };

    /* --- Track transform (no CSS transition; follows scroll directly) --- */
    const setOffset = (px) => {
      track.style.transition = 'none';
      track.style.transform = `translate3d(${-px}px, 0, 0)`;
    };

    /* --- Read scroll progress + paint --- */
    const update = () => {
      const max = maxIndex();
      if (max === 0) {
        setOffset(0);
        currentIndex = 0;
        currentProgress = 0;
        updateRail();
        updateButtons();
        return;
      }
      const rect = pin.getBoundingClientRect();
      const pinTravel = pin.clientHeight - window.innerHeight;
      const scrolled = -rect.top;
      const progress = pinTravel > 0
        ? Math.max(0, Math.min(1, scrolled / pinTravel))
        : 0;

      currentProgress = progress;
      currentIndex = Math.round(progress * max);

      setOffset(progress * maxOffset());
      updateRail();
      updateButtons();
    };

    const updateRail = () => {
      if (!bar) return;
      const railWidth = bar.parentElement.clientWidth;
      const barWidth = bar.offsetWidth;
      const travel = Math.max(0, railWidth - barWidth);
      bar.style.transform = `translateX(${(currentProgress * travel).toFixed(2)}px)`;
    };

    const updateButtons = () => {
      const max = maxIndex();
      if (prevBtn) prevBtn.toggleAttribute('disabled', currentIndex <= 0);
      if (nextBtn) nextBtn.toggleAttribute('disabled', currentIndex >= max);
    };

    /* --- Buttons scroll the page to the target index's progress position --- */
    const scrollToIndex = (i) => {
      const max = maxIndex();
      if (max === 0) return;
      const targetIdx = Math.max(0, Math.min(i, max));
      const targetProgress = targetIdx / max;
      const pinTop = pin.getBoundingClientRect().top + window.scrollY;
      const pinTravel = pin.clientHeight - window.innerHeight;
      window.scrollTo({
        top: pinTop + targetProgress * pinTravel,
        behavior: 'smooth',
      });
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => scrollToIndex(currentIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => scrollToIndex(currentIndex + 1));
    }

    /* --- rAF-throttled scroll listener --- */
    let rafId = null;
    const onScroll = () => {
      if (rafId !== null) return;
      rafId = requestAnimationFrame(() => {
        update();
        rafId = null;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });

    /* --- Resize: recompute pin height + repaint --- */
    let resizeTimer;
    const onResize = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        setPinHeight();
        update();
      }, 80);
    };
    window.addEventListener('resize', onResize);

    /* --- Initial paint (twice: once for layout, once for image load) --- */
    requestAnimationFrame(() => {
      setPinHeight();
      update();
    });
    window.addEventListener('load', () => {
      setPinHeight();
      update();
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
